import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileText } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { useAuth } from "@/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DocumentNumberLink from "@/components/DocumentNumberLink";
import Pagination from "@/components/Pagination";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import CustomerLedgerPrint, {
  type LedgerPrintData,
} from "@/components/CustomerLedgerPrint";
import { useClearFilterParams, useFilterParam } from "@/hooks/useFilterParam";
import { usePagination } from "@/hooks/usePagination";
import { formatLedgerBalance, formatNumber } from "@/lib/format";
import { renderReactToPdf, safePdfFilename } from "@/lib/htmlPdf";
import type { PdfCompany } from "@/lib/pdf";

interface LedgerRow {
  date: string;
  type: "invoice" | "receipt" | "reversal" | "other";
  reference: string;
  description: string;
  debit: number;
  credit: number;
  running: number;
  id: number | null;
}

interface LedgerResponse {
  customer: { id: number; name: string };
  date_from: string | null;
  date_to: string | null;
  opening_balance: number;
  rows: LedgerRow[];
  totals: {
    total_debit: number;
    total_credit: number;
    closing_balance: number;
  };
}

const FILTER_KEYS = ["date_from", "date_to"] as const;

// dd-mm-yyyy display to match the rest of the document tables.
function formatDmy(input?: string | null) {
  if (!input) return "—";
  const s = String(input);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
}

export default function CustomerLedger() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { user, can } = useAuth();
  const canOpenInvoice = can("sales_invoices.read");
  const canOpenReceipt = can("receipts.read");

  const [dateFrom, setDateFrom] = useFilterParam("date_from");
  const [dateTo, setDateTo] = useFilterParam("date_to");
  const clearFilters = useClearFilterParams(FILTER_KEYS);

  const [data, setData] = useState<LedgerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<PdfCompany>({});

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState("");
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const hasAnyFilter = Boolean(dateFrom || dateTo);

  // Paginate displayed transaction rows only; the opening-balance row and the
  // totals footer are computed over the full statement and stay visible on
  // every page. Running balances are precomputed server-side, so slicing the
  // display never changes them.
  const pg = usePagination(data?.rows ?? []);

  useEffect(() => {
    pg.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Currency symbol for money columns — defaults to the app currency (GH₵).
  const currencySymbol = (company.currency_symbol || "GH₵").trim() || "GH₵";

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ settings: Record<string, string> }>(
          "/settings",
        );
        const s = res.settings ?? {};
        setCompany({
          name: s["company.name"] || undefined,
          email: s["company.email"] || undefined,
          phone: s["company.phone"] || undefined,
          address: s["company.address"] || undefined,
          currency_code: s["currency.code"] || undefined,
          currency_symbol: s["currency.symbol"] || undefined,
        });
      } catch {
        // Non-fatal — the statement still renders with default branding.
      }
    })();
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams();
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
        const qs = params.toString();
        const res = await api.get<LedgerResponse>(
          `/customers/${id}/ledger${qs ? `?${qs}` : ""}`,
        );
        setData(res);
      } catch (err) {
        toast.error((err as Error).message || "Failed to load ledger");
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, dateFrom, dateTo]);

  const customerName = data?.customer.name ?? "";
  const showOpening = Boolean(dateFrom);

  const printData: LedgerPrintData | null = useMemo(() => {
    if (!data) return null;
    return {
      customerName: data.customer.name,
      dateFrom: data.date_from,
      dateTo: data.date_to,
      openingBalance: data.opening_balance,
      rows: data.rows.map((r) => ({
        date: r.date,
        type: r.type,
        reference: r.reference,
        description: r.description,
        debit: r.debit,
        credit: r.credit,
        running: r.running,
      })),
      totals: data.totals,
    };
  }, [data]);

  function resolvePreparedBy(): string {
    return (
      user?.name?.trim() ||
      user?.username?.trim() ||
      user?.email?.trim() ||
      "—"
    );
  }

  async function buildPdf() {
    if (!printData) throw new Error("Ledger not loaded");
    const printCompany = {
      name: (company.name || "BillForge").toString(),
      address: (company.address || "").toString(),
      phone: (company.phone || "").toString(),
      email: (company.email || "").toString(),
      currencySymbol,
    };
    return renderReactToPdf(
      <CustomerLedgerPrint
        data={printData}
        company={printCompany}
        preparedBy={resolvePreparedBy()}
      />,
      {
        filename: safePdfFilename(
          "Statement",
          customerName || `Customer-${id}`,
        ),
      },
    );
  }

  async function handlePreviewPdf() {
    if (generatingPdf || !printData) return;
    setGeneratingPdf(true);
    try {
      const pdf = await buildPdf();
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(pdf.url);
      setPdfFilename(pdf.filename);
      setPdfModalOpen(true);
    } catch (err) {
      toast.error((err as Error).message || "Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  }

  async function handleDownloadPdf() {
    if (generatingPdf || !printData) return;
    setGeneratingPdf(true);
    try {
      const pdf = await buildPdf();
      const a = document.createElement("a");
      a.href = pdf.url;
      a.download = pdf.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(pdf.url);
      toast.success("Statement downloaded");
    } catch (err) {
      toast.error((err as Error).message || "Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  }

  function handlePdfModalOpenChange(open: boolean) {
    if (!open) {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
      setPdfFilename("");
    }
    setPdfModalOpen(open);
  }

  function referenceTo(row: LedgerRow): string {
    if (row.type === "invoice" && row.id) return `/sales-invoices/${row.id}`;
    if (row.type === "receipt" && row.id) return `/receipts/${row.id}`;
    return "#";
  }

  function canOpenRow(row: LedgerRow): boolean {
    if (row.id == null) return false;
    return row.type === "invoice" ? canOpenInvoice : row.type === "receipt" ? canOpenReceipt : false;
  }

  function rowTypeLabel(row: LedgerRow): string {
    if (row.type === "invoice") return "Sales Invoice";
    if (row.type === "receipt") return "Payment Receipt";
    if (row.type === "reversal") return "Reversal";
    return row.description || "Entry";
  }

  const outstanding = data?.totals.closing_balance ?? 0;
  const settled = outstanding <= 0.01;

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav("/customers")}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Statement of Account
            </h1>
            <p className="text-muted-foreground">
              {customerName || (loading ? "Loading…" : "Customer ledger")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handlePreviewPdf}
            disabled={generatingPdf || !data}
          >
            <FileText className="h-4 w-4 mr-2" />
            {generatingPdf ? "Preparing..." : "Preview PDF"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleDownloadPdf}
            disabled={generatingPdf || !data}
          >
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Total Invoiced
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono">
              {currencySymbol} {formatNumber(data?.totals.total_debit ?? 0)}
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Total Paid
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-emerald-600 dark:text-emerald-400">
              {currencySymbol} {formatNumber(data?.totals.total_credit ?? 0)}
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Outstanding Balance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold font-mono ${
                settled
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-rose-600 dark:text-rose-400"
              }`}
            >
              {currencySymbol} {formatLedgerBalance(outstanding)}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[280px_minmax(0,1fr)]">
        <div className="overflow-hidden rounded-md border border-border bg-amber-50/40 dark:bg-amber-950/10 shadow-card">
          <div className="border-b border-border bg-amber-100 px-3 py-2 dark:bg-amber-900/30">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-100">
              Filter Statement
            </h2>
          </div>
          <div className="space-y-2 p-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <span className="block text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  From Date
                </span>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-9 w-full cursor-pointer bg-background [color-scheme:light] dark:[color-scheme:dark]"
                  aria-label="From date"
                  max={dateTo || undefined}
                />
              </div>
              <div className="space-y-1">
                <span className="block text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  To Date
                </span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-9 w-full cursor-pointer bg-background [color-scheme:light] dark:[color-scheme:dark]"
                  aria-label="To date"
                  min={dateFrom || undefined}
                />
              </div>
            </div>
            {hasAnyFilter ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="mt-1 w-full"
              >
                Clear filters
              </Button>
            ) : null}
          </div>
        </div>
        <Card className="shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b bg-muted/40 text-left">
                  <th className="px-3 py-2.5 font-medium w-28">Date</th>
                  <th className="px-3 py-2.5 font-medium">Description</th>
                  <th className="px-3 py-2.5 font-medium text-right w-32">
                    Debit
                  </th>
                  <th className="px-3 py-2.5 font-medium text-right w-32">
                    Credit
                  </th>
                  <th className="px-3 py-2.5 font-medium text-right w-32">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-3 py-10 text-center text-muted-foreground"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : null}
                {!loading && showOpening && data ? (
                  <tr className="border-b bg-muted/20">
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {formatDmy(data.date_from)}
                    </td>
                    <td className="px-3 py-2.5 italic text-muted-foreground">
                      Opening Balance
                    </td>
                    <td className="px-3 py-2.5 text-right text-muted-foreground">
                      —
                    </td>
                    <td className="px-3 py-2.5 text-right text-muted-foreground">
                      —
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold">
                      {formatLedgerBalance(data.opening_balance)}
                    </td>
                  </tr>
                ) : null}
                {!loading && data && data.rows.length === 0 && !showOpening ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-3 py-10 text-center text-muted-foreground"
                    >
                      No transactions for this customer.
                    </td>
                  </tr>
                ) : null}
                {!loading &&
                  pg.pageItems.map((r, i) => (
                    <tr key={`${r.type}-${r.id}-${i}`} className="border-b">
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {formatDmy(r.date)}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-col">
                          <span>{rowTypeLabel(r)}</span>
                          {r.reference ? (
                            canOpenRow(r) ? (
                              <DocumentNumberLink
                                to={referenceTo(r)}
                                canOpen
                                className="text-xs"
                              >
                                {r.reference}
                              </DocumentNumberLink>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                {r.reference}
                              </span>
                            )
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono">
                        {r.debit > 0 ? formatNumber(r.debit) : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-emerald-600 dark:text-emerald-400">
                        {r.credit > 0 ? formatNumber(r.credit) : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono font-semibold">
                        {formatLedgerBalance(r.running)}
                      </td>
                    </tr>
                  ))}
              </tbody>
              {!loading && data ? (
                <tfoot>
                  <tr className="border-t-2 bg-muted/40 font-semibold">
                    <td className="px-3 py-2.5" colSpan={2}>
                      Totals
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {formatNumber(data.totals.total_debit)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {formatNumber(data.totals.total_credit)}
                    </td>
                    <td
                      className={`px-3 py-2.5 text-right font-mono font-bold ${
                        settled
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-rose-600 dark:text-rose-400"
                      }`}
                    >
                      {formatLedgerBalance(data.totals.closing_balance)}
                    </td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
          {!loading && data && data.rows.length > 0 ? (
            <Pagination
              page={pg.page}
              pageCount={pg.pageCount}
              total={pg.total}
              from={pg.from}
              to={pg.to}
              onPrev={pg.prev}
              onNext={pg.next}
            />
          ) : null}
        </Card>
      </div>

      <PdfPreviewModal
        open={pdfModalOpen}
        onOpenChange={handlePdfModalOpenChange}
        pdfUrl={pdfPreviewUrl}
        filename={pdfFilename}
        title="Statement of Account"
        documentNumber={customerName}
      />
    </div>
  );
}
